import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { RotatingTagline } from "@/components/RotatingTagline";
import { isDark, DEFAULT_PROMO, visibleColumns } from "@/lib/theme";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const tid = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function useIdleClose(ms: number, onClose: () => void) {
  const t = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cancel = () => clearTimeout(t.current);
  const start = (n = ms) => { cancel(); t.current = setTimeout(onClose, n); };
  useEffect(() => cancel, []);
  return { start, cancel };
}

function MegaMenu({ menu, closeMs, touchMs }: { menu: any[]; closeMs: number; touchMs: number }) {
  const [active, setActive] = useState<number | null>(null);
  const idle = useIdleClose(closeMs, () => setActive(null));
  const touch = (e: React.PointerEvent) => e.pointerType !== "mouse";
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (active === null) return;
    const out = (e: PointerEvent) => { if (e.pointerType !== "mouse" && !navRef.current?.contains(e.target as Node)) { idle.cancel(); setActive(null); } };
    document.addEventListener("pointerdown", out);
    return () => document.removeEventListener("pointerdown", out);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <nav ref={navRef} className="hidden lg:flex justify-center gap-4 xl:gap-6 2xl:gap-10 relative -mt-[7px]" onPointerEnter={(e) => !touch(e) && idle.cancel()} onPointerLeave={(e) => !touch(e) && idle.start()} onPointerDown={(e) => touch(e) && idle.start(touchMs)} data-testid="desktop-nav">
      {menu.map((m, i) => (
        <div key={m.label} onMouseEnter={() => setActive(i)} onPointerDown={(e) => touch(e) && setActive(i)}>
          <Link to={m.url} className="eyebrow pt-0 pb-4 inline-block whitespace-nowrap lg:!text-[10px] lg:!tracking-[0.16em] xl:!text-[11px] xl:!tracking-[0.28em] link-u hover:text-[var(--c-primary)] transition-colors" data-testid={`nav-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {m.label}
          </Link>
        </div>
      ))}
      {active !== null && menu[active]?.columns?.length > 0 && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-screen bg-[var(--c-bg)] border-y border-neutral-200 shadow-sm z-40" data-testid="mega-menu-panel">
          <div className={`mx-auto px-12 py-10 grid gap-10 ${menu[active].columns.length > 3 ? "max-w-[1400px]" : "max-w-[1200px]"}`} style={{ gridTemplateColumns: `repeat(${Math.max(4, Math.min(menu[active].columns.length, 5) + 1)}, minmax(0, 1fr))` }}>
            {menu[active].columns.slice(0, 5).map((c: any) => (
              <div key={c.title}>
                <p className="eyebrow text-[var(--c-primary)] mb-5">{c.title}</p>
                <ul className="space-y-3">
                  {c.links.map((l: any) => (
                    <li key={l.label}><Link to={l.url} onClick={() => setActive(null)} className="font-display text-lg hover:text-[var(--c-primary)] transition-colors">{l.label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
            {(() => { const p = menu[active].promo || DEFAULT_PROMO; return p.title ? (
              <Link to={p.url || "/"} onClick={() => setActive(null)} className="group relative col-start-[-2] bg-[var(--c-muted)] p-8 flex flex-col justify-end overflow-hidden min-h-[220px] hover:bg-neutral-200 transition-colors" data-testid="mega-menu-promo">
                {p.image && <><img src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid="mega-menu-promo-image" /><span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" /></>}
                {p.eyebrow && <p className={`eyebrow mb-2 relative ${p.image ? "!text-white" : ""}`}>{p.eyebrow}</p>}
                <p className={`font-display text-2xl leading-tight relative ${p.image ? "!text-white" : ""}`}>{p.title}</p>
              </Link>) : null; })()}
          </div>
        </div>
      )}
    </nav>
  );
}

function MobileMenu({ menu, open, onOpenChange, closeMs }: any) {
  const [exp, setExp] = useState<string | null>(null);
  const idle = useIdleClose(closeMs, () => onOpenChange(false));
  useEffect(() => { if (open) idle.start(); else idle.cancel(); }, [open, closeMs]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" onPointerDown={() => idle.start()} onPointerMove={() => idle.start()} onScroll={() => idle.start()} onKeyDown={() => idle.start()} className="bg-[var(--c-bg)] w-[88vw] sm:max-w-sm overflow-y-auto" data-testid="mobile-menu">
        <SheetTitle className="font-display text-2xl tracking-[0.2em] mb-8">MENU</SheetTitle>
        {menu.map((m: any) => (
          <div key={m.label} className="border-b border-neutral-200">
            <div className="flex items-center justify-between py-4">
              <Link to={m.url} onClick={() => onOpenChange(false)} className="font-display text-xl" data-testid={`mobile-menu-link-${tid(m.label)}`}>{m.label}</Link>
              {m.columns?.length > 0 && (
                <button onClick={() => setExp(exp === m.label ? null : m.label)} aria-label={`Show ${m.label} links`} aria-expanded={exp === m.label} className="h-10 w-10 -mr-2 grid place-items-center" data-testid={`mobile-menu-expand-${tid(m.label)}`}><ChevronDown size={18} className={exp === m.label ? "rotate-180 transition-transform" : "transition-transform"} /></button>
              )}
            </div>
            {exp === m.label && m.columns.map((c: any, ci: number) => (
              <div key={c.title} className="pb-4 pl-2">
                <p className="eyebrow text-neutral-500 mb-2">{c.title}</p>
                {c.links.map((l: any) => <Link key={l.label} to={l.url} onClick={() => onOpenChange(false)} className="block py-1.5 text-sm" data-testid={`mobile-menu-sublink-${ci}-${tid(l.label)}`}>{l.label}</Link>)}
              </div>
            ))}
          </div>
        ))}
        <div className="pt-6 space-y-3 text-sm">
          <Link to="/consultation" onClick={() => onOpenChange(false)} className="block">Book a Consultation</Link>
          <Link to="/stores" onClick={() => onOpenChange(false)} className="block">Showrooms</Link>
          <Link to="/track" onClick={() => onOpenChange(false)} className="block">Track an Order</Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function Header() {
  const { site } = useSite();
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const today = new Date().toLocaleDateString("en-CA");
  const closeMs = (Number(site?.menu_close_seconds) || 3) * 1000;
  const touchMs = (Number(site?.menu_close_seconds_touch) || 5) * 1000;
  const menu = (site?.header_menu || []).filter((m: any) => !m.hidden && (!m.show_from || m.show_from <= today) && (!m.show_until || m.show_until >= today))
    .map((m: any) => ({ ...m, columns: visibleColumns(m.columns) }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearching(false);
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <AnnouncementBar site={site} />
      <header className="sticky top-0 z-50 bg-[var(--c-header-bg)] text-[var(--c-header-fg)] backdrop-blur-xl border-b border-neutral-200/70" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 h-16 md:h-20 grid grid-cols-3 items-center">
          <div className="flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-button"><Menu size={22} strokeWidth={1.3} /></button>
            <Link to="/consultation" className="hidden lg:inline eyebrow text-[10px] link-u">Book Appointment</Link>
          </div>
          <Link to="/" className="justify-self-center flex flex-col items-center" data-testid="brand-logo">
            <img src={site?.logo_url || "/logo-somnio.png"} alt={site?.brand || "Somnio.Co"} className="h-[14.4px] md:h-[19.2px] w-auto select-none" style={isDark(site?.theme?.header_bg) ? { filter: "invert(1)" } : undefined} draggable={false} />
            <RotatingTagline site={site} />
          </Link>
          <div className="flex items-center justify-end gap-4 md:gap-5">
            <button onClick={() => setSearching(true)} aria-label="Search" data-testid="search-open-button" className="hover:text-[var(--c-primary)] transition-colors"><Search size={20} strokeWidth={1.3} /></button>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"} aria-label="Account" data-testid="header-account-link" className="hover:text-[var(--c-primary)] transition-colors"><User size={20} strokeWidth={1.3} /></Link>
            <Link to="/account?tab=wishlist" aria-label="Wishlist" data-testid="header-wishlist-link" className="hidden sm:block hover:text-[var(--c-primary)] transition-colors"><Heart size={20} strokeWidth={1.3} /></Link>
            <button onClick={() => setOpen(true)} aria-label="Shopping bag" className="relative hover:text-[var(--c-primary)] transition-colors" data-testid="header-cart-button">
              <ShoppingBag size={20} strokeWidth={1.3} />
              {count > 0 && <span className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-[var(--c-primary)] text-white text-[10px] grid place-items-center" data-testid="cart-count">{count}</span>}
            </button>
          </div>
        </div>
        <MegaMenu menu={menu} closeMs={closeMs} touchMs={touchMs} />
        {searching && (
          <div className="absolute inset-x-0 top-0 h-full min-h-20 bg-[var(--c-bg)] z-50 flex items-center px-5 md:px-12" data-testid="search-overlay">
            <form onSubmit={submit} className="flex-1 flex items-center gap-4 max-w-[1400px] mx-auto">
              <Search size={20} strokeWidth={1.3} />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rings, earrings, shapes…" className="flex-1 bg-transparent font-display text-2xl outline-none" data-testid="search-input" />
              <button type="button" onClick={() => setSearching(false)} aria-label="Close search" data-testid="search-close-button"><X size={20} /></button>
            </form>
          </div>
        )}
      </header>
      <MobileMenu menu={menu} open={mobile} onOpenChange={setMobile} closeMs={closeMs} />
    </>
  );
}
